import React, { useState, useRef, useMemo, useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useScrollSnap } from '../hooks/useScrollSnap';
import { useRemountOnPageHidden } from '../hooks/useRemountOnPageHidden';
import {
  IonContent,
  IonList,
  IonItem,
  IonLabel,
  IonSearchbar,
  IonIcon,
  IonText,
  IonAvatar,
  IonGrid,
  IonButton,
  IonChip,
  IonBadge,
  IonSkeletonText,
  IonRefresherContent,
  IonModal,
  IonFab,
  IonToolbar,
  IonButtons,
} from '@ionic/react';
import { chatbubbleOutline, searchOutline, closeOutline, createOutline, filterOutline, star, checkmarkSharp, archiveOutline, ellipsisVertical, trashOutline, call, mail, mailOpen, person } from 'ionicons/icons';
import styles from '../pages/Inbox.module.scss';
import { Conversation, Message } from '@rsmaids/shared/types/Inbox';
import { InboxService } from '@rsmaids/shared/services/api/inboxService';
import BasePageWithHeader, { TitleCard } from './BasePageWithHeader';
import SheetModal from './SheetModal';
import ContactPickerSheet from './ContactPickerSheet';
import PositionedRefresher from './PositionedRefresher';
import InboxSettingsModal from './InboxSettingsModal';
import InboxNotificationsSettingsModal from './InboxNotificationsSettingsModal';
import { TemplatesModal } from './ConversationComponents';
import type { ContactListItem } from '@rsmaids/shared/types/contacts';
import { DataServiceFactory } from '@rsmaids/shared/services/DataServiceFactory';
import { getDefaultAvatar, proxyImage } from '@rsmaids/shared/utils/avatarUtils';
import { useUnifiedInbox } from '../hooks/useUnifiedInbox';
import { markConversationTap } from '../utils/openTiming';
import { useKeyboardHeight, KEYBOARD_GAP_PX } from '../hooks/useKeyboardHeight';
import { useSmsInbox, markSmsRead } from '../hooks/useSmsInbox';
import { getCustomerInfo, onCacheInvalidation } from '../services/UnifiedInboxService';
import {
  ensurePlaceholderConversation,
  getConversationMeta,
  getLastSelectedInboxConversationId,
  mergeConversationListsKeepLocalsFirst,
} from '../utils/inboxSessionUtils';
import { useTranslation } from 'react-i18next';
import { intlLocale } from '../i18n/locale';
import { formatBusinessNotificationText } from '../services/messageFormatting';
import { getFirestore, doc, updateDoc } from 'firebase/firestore';
import { getApp } from 'firebase/app';
import { auth } from '../auth/firebase';
import { INBOX_ROLES } from '@rsmaids/shared/constants/roles';
import { useRoleAccess } from '../hooks/useRoleAccess';
import AccessDeniedPage from './AccessDeniedPage';

// Saves archive / unarchive on the conversation document (shared by all staff; the list reads `archived` from it).
// Text-only rows (sms-…, timeline-…, temp-…) have no wix-chat document and are skipped.
function saveRowsArchived(rows: Conversation[], archived: boolean): void {
  const db = getFirestore(getApp(), 'wix-chat');
  rows.forEach(row => {
    const id = String(row.id);
    if (id.startsWith('sms-') || id.startsWith('temp-') || id.startsWith('timeline-')) return;
    updateDoc(doc(db, 'conversations', id), { archived })
      .catch((e: { code?: string }) => {
        if (e?.code !== 'not-found') console.error('[saveRowsArchived] could not save archive for', id, e);
      });
  });
}

// Saves "read" for the signed-in staff member only (others keep their own unread): a text row gets its per-staff read
// marker, a chat conversation has its `unreadBy.<uid>` counter set to 0 (the same two things opening a thread does).
// Rows that merge a contact's chat and texts clear both. Ids without a wix-chat document are skipped.
function saveRowsRead(rows: Array<Conversation & { smsPhone?: string; _mergedSmsPhones?: string[] }>): void {
  const myUid = auth.currentUser?.uid;
  const db = getFirestore(getApp(), 'wix-chat');
  rows.forEach(row => {
    if (row.smsPhone) void markSmsRead(row.smsPhone);
    row._mergedSmsPhones?.forEach(phone => { void markSmsRead(phone); });
    const id = String(row.id);
    if (!myUid || id.startsWith('sms-') || id.startsWith('temp-') || id.startsWith('timeline-')) return;
    updateDoc(doc(db, 'conversations', id), { [`unreadBy.${myUid}`]: 0 })
      .catch((e: { code?: string }) => {
        // not-found: no conversation document for this id (a draft or a contact who only has texts) — nothing to clear
        if (e?.code !== 'not-found') console.error('[saveRowsRead] could not clear unread for', id, e);
      });
  });
}

// Type for unified inbox item customer info
interface UnifiedConvInfo {
  customerId?: string;
  phoneNumber?: string;
  isNonContact: boolean;
}

// Type for customer cache
interface CustomerInfo {
  id: string;
  name: string;
  avatar?: string;
  phone?: string;
  email?: string;
}

// A display name is only "real" if it has letters in it — a formatted phone number
// (e.g. "(302) 897-8052") shouldn't be run through initials-avatar generation, since that
// produces nonsense like "(8". Matches the same check useSmsInbox.ts uses for hasContactName.
function isRealDisplayName(name: string | undefined): boolean {
  return !!name && /[a-zA-Z]/.test(name);
}

function formatTimestamp(date: Date | undefined): string {
  if (!date) return 'now';
  const now = new Date();
  const diff = now.getTime() - date.getTime();
  const minutes = Math.floor(diff / (1000 * 60));
  const hours = Math.floor(diff / (1000 * 60 * 60));
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));

  if (minutes < 1) return 'now';
  if (minutes < 60) return `${minutes}m`;
  if (hours < 24) return `${hours}h`;
  if (days < 7) return `${days}d`;
  return date.toLocaleDateString(intlLocale());
}

/**
 * Which date section a conversation belongs to in the list (Today, Yesterday, Previous 7/30 days, then
 * one section per month). The list is sorted newest-first, so a section is a run of consecutive rows.
 */
function getDateGroup(
  date: Date | undefined,
  now: Date,
  t: (key: string) => string,
): { key: string; label: string } {
  if (!date) return { key: 'today', label: t('components.InboxConversationList.today') };
  const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const dayDiff = Math.round((startOfDay(now) - startOfDay(date)) / 86_400_000);
  if (dayDiff <= 0) return { key: 'today', label: t('components.InboxConversationList.today') };
  if (dayDiff === 1) return { key: 'yesterday', label: t('components.InboxConversationList.yesterday') };
  if (dayDiff <= 7) return { key: 'last7', label: t('components.InboxConversationList.previous7Days') };
  if (dayDiff <= 30) return { key: 'last30', label: t('components.InboxConversationList.previous30Days') };
  return {
    key: `${date.getFullYear()}-${date.getMonth()}`,
    label: date.toLocaleDateString(intlLocale(), { month: 'long', year: 'numeric' }),
  };
}

export interface InboxConversationSelectMeta {
  /** The conversation's contact, when the list already knows it — saves the thread looking it up */
  contactId?: string;
  contactName?: string;
  avatarUrl?: string;
  isBlankShell?: boolean;
  /** Phone numbers (E.164) of the texts this row folded in — lets the thread load them without a lookup */
  smsPhones?: string[];
  initialChannel?: 'chat' | 'email' | 'sms';
  initialFromEmail?: 'scheduling' | 'billing' | 'support';
}

interface InboxConversationListProps {
  onSelectConversation: (id: string, meta: InboxConversationSelectMeta) => void;
  /** When set (e.g. inside the softphone modal), a close button is shown next to the create button. */
  onClose?: () => void;
}

const InboxConversationList: React.FC<InboxConversationListProps> = ({ onSelectConversation, onClose }) => {
  const { t } = useTranslation();
  const [searchText, setSearchText] = useState('');
  // If we land on the inbox list right after a desktop-created conversation,
  // Firestore may not include it yet. Use the last-selected id + persisted
  // meta to keep a row visible — read directly in the lazy initializer (not
  // via a mount effect) since it only ever needs to happen once.
  const [conversations, setConversations] = useState<Conversation[]>(() => {
    const lastSelectedId = getLastSelectedInboxConversationId();
    if (!lastSelectedId) return [];
    const meta = getConversationMeta(lastSelectedId);
    if (!meta?.contactName) return [];
    return ensurePlaceholderConversation([], String(lastSelectedId), meta, (id, m) => ({
      id,
      name: m.contactName,
      messages: [],
      timestamp: new Date(),
      avatarUrl: m.avatarUrl || '',
      lastMessage: '',
    }));
  });
  const [conversationDrafts, setConversationDrafts] = useState<Conversation[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  // Who may use the Inbox: the sign-in token's role, checked before any of the page shows (see useRoleAccess). `deny` is for when
  // Firestore itself refuses the live conversations.
  const { access, deny } = useRoleAccess(INBOX_ROLES);
  const [isSearchVisible, setIsSearchVisible] = useState(false);
  const [isSearchClosing, setIsSearchClosing] = useState(false);
  const [isSearchHidden, setIsSearchHidden] = useState(false);
  const [showContactPicker, setShowContactPicker] = useState(false);
  const openContactPicker = () => {
    markConversationTap();
    setShowContactPicker(true);
  };
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const menuButtonRef = useRef<HTMLIonButtonElement>(null);
  const menuCardRef = useRef<HTMLDivElement>(null);

  // ⋮ menu focus: move focus into the menu when it opens, close on Escape, and hand focus back to the
  // ⋮ button when it closes (outside taps are caught by the menu's backdrop)
  useEffect(() => {
    if (!showMenu) return;
    const button = menuButtonRef.current;
    menuCardRef.current?.querySelector<HTMLButtonElement>('button')?.focus();
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setShowMenu(false);
    };
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      button?.focus();
    };
  }, [showMenu]);
  const [showInboxSettingsModal, setShowInboxSettingsModal] = useState(false);
  const [showInboxNotificationsSettingsModal, setShowInboxNotificationsSettingsModal] = useState(false);
  const [showTemplatesModal, setShowTemplatesModal] = useState(false);
  const [activeFilter, setActiveFilter] = useState('all');
  const [editMode, setEditMode] = useState(false);
  const [selectedConversations, setSelectedConversations] = useState<(string | number)[]>([]);
  const baseHeaderRef = useRef<HTMLIonHeaderElement>(null);
  const titleCardAnimationRef = useRef<HTMLIonCardElement>(null);
  const gridRef = useRef<HTMLIonGridElement>(null);
  const titleCardRef = useRef<HTMLDivElement>(null);
  const overlayRef = useRef<HTMLElement | null>(null);
  // The native keyboard doesn't resize the screen, so the search bar (pinned to the bottom) is lifted
  // by the keyboard's height, and the list gets matching bottom padding — as in BookingsListModal.
  const keyboardHeight = useKeyboardHeight();
  const searchRef = useRef<HTMLIonSearchbarElement | null>(null);
  const hasInitialLoadRef = useRef(false); // Track if initial load is complete to prevent double-fetch
  const wasAtTop = useRef(true);
  const [refresherDisabled, setRefresherDisabled] = useState(false);

  // Load unified timeline data (SMS, calls, Wix messages) from all customers
  const { items: unifiedItems } = useUnifiedInbox({
    days: 30,
    limit: 200,
    autoSubscribe: true,
  });

  // Subscribe to Twilio SMS conversations from sms-conversations collection
  const { conversations: smsConversations } = useSmsInbox();

  // Helper function to normalize timestamps to Date objects
  const normalizeConversations = (conversations: Conversation[]): Conversation[] => {
    return conversations.map(c => ({
      ...c,
      timestamp: c.timestamp instanceof Date ? c.timestamp : new Date(String(c.timestamp))
    }));
  };

  // Load conversations from Wix on mount and subscribe to real-time updates
  React.useEffect(() => {
    const loadConversations = async () => {
      setIsLoading(true);
      try {
        const dataService = DataServiceFactory.getService();
        // Pass callback to update UI as batches complete
        const data = await dataService.getConversations?.(
          (updatedConversations: Conversation[]) => {
            // Normalize timestamps to Date objects
            const normalized = normalizeConversations(updatedConversations);
            // Update UI with latest batch (sorting happens in useMemo)
            setConversations(prev => mergeConversationListsKeepLocalsFirst(normalized, prev, undefined));
            console.log(`📭 UI updated with ${normalized.length} conversations`);
            if (normalized.length > 0) {
              console.log('📋 Callback data order (first 5):', normalized.slice(0, 5).map(c => ({
                name: c.name,
                timestamp: c.timestamp?.toISOString?.()
              })));
            }
          }
        );
        // Final update with all conversations (sorting happens in useMemo)
        const normalized = normalizeConversations(data || []);
        setConversations(prev => mergeConversationListsKeepLocalsFirst(normalized, prev, undefined));
        setIsLoading(false);
        hasInitialLoadRef.current = true; // Mark initial load complete
      } catch (error) {
        console.error('Failed to load conversations:', error);
        setIsLoading(false);
        hasInitialLoadRef.current = true; // Mark initial load complete even on error
      }
    };

    loadConversations();

    // Subscribe to real-time conversation updates from Firestore (canonical)
    console.log('📭 Subscribing to real-time conversation updates...');
    let cancelled = false;
    let retriedWithFreshToken = false;
    let stopConversations = () => {};
    const subscribeConversations = () => {
      stopConversations = InboxService.subscribeToConversations((updatedConversations) => {
        console.log(`📭 Real-time update: ${updatedConversations.length} conversations`);
        const normalized = normalizeConversations(updatedConversations);
        setConversations(prev => mergeConversationListsKeepLocalsFirst(normalized, prev, undefined));
      }, (error) => {
        if ((error as { code?: string })?.code !== 'permission-denied') return;
        const denyAccess = () => {
          if (cancelled) return;
          deny();
          setConversations([]);
          setIsLoading(false);
        };
        // The role on the sign-in token may have only just changed: ask for a fresh token once before deciding access is denied
        if (!retriedWithFreshToken && auth.currentUser) {
          retriedWithFreshToken = true;
          stopConversations();
          auth.currentUser.getIdToken(true)
            .then(() => { if (!cancelled) subscribeConversations(); })
            .catch(denyAccess);
          return;
        }
        denyAccess();
      });
    };
    subscribeConversations();

    // Subscribe to conversationDrafts for instant UI updates
    console.log('📝 Subscribing to conversationDrafts for optimistic UI...');
    const unsubscribeDrafts = InboxService.subscribeToConversationDrafts?.((updatedDrafts) => {
      console.log(`📝 Real-time draft update: ${updatedDrafts.length} drafts`);
      const normalized = normalizeConversations(updatedDrafts);
      setConversationDrafts(normalized);
    }) || (() => {});

    // Cleanup subscriptions on unmount
    return () => {
      console.log('📭 Unsubscribing from conversation updates');
      cancelled = true;
      stopConversations();
      console.log('📝 Unsubscribing from draft updates');
      unsubscribeDrafts();
    };
  }, [deny]);

  // (There used to be a refetch here every time this screen was re-entered. Nothing needs it: the page stays
  // mounted, and the conversations, drafts and SMS listeners above keep the list current — so coming back from
  // a conversation now costs no request.)

  // Overlay positioning for morphing search (full width with gap for filter button)
  const updateOverlayPosition = useCallback(() => {
    if (!overlayRef.current) return;
    // The list's own width (the full screen on mobile; a side panel on desktop)
    const viewportWidth = baseHeaderRef.current?.getBoundingClientRect().width || window.innerWidth;
    if (isSearchVisible) {
      // 16px margin left + 16px margin right + 8px gap + 40px filter button
      const desiredWidth = viewportWidth - 32 - 48;
      overlayRef.current.style.setProperty('--overlay-w', `${desiredWidth}px`);
      overlayRef.current.style.setProperty('--overlay-left', '16px');
    } else {
      overlayRef.current.style.setProperty('--overlay-w', '56px');
      const centerLeft = (viewportWidth / 2) - 28;
      overlayRef.current.style.setProperty('--overlay-left', `${centerLeft}px`);
    }
  }, [isSearchVisible]);

  React.useEffect(() => {
    updateOverlayPosition();
    window.addEventListener('resize', updateOverlayPosition);
    return () => window.removeEventListener('resize', updateOverlayPosition);
  }, [updateOverlayPosition]);

  React.useEffect(() => {
    const id = window.setTimeout(() => updateOverlayPosition(), 30);
    return () => window.clearTimeout(id);
  }, [isSearchVisible, updateOverlayPosition]);

  const handleToggleSearch = useCallback(() => {
    if (isSearchVisible) {
      // Clear input
      if (searchRef.current) {
        try { (searchRef.current as HTMLIonSearchbarElement).value = ''; } catch { }
      }
      setSearchText('');
      setActiveFilter('all');
      // Start closing animation
      setIsSearchClosing(true);
      setIsSearchVisible(false);
      setTimeout(() => {
        setIsSearchHidden(true);
        setIsSearchClosing(false);
      }, 220);
    } else {
      setIsSearchHidden(false);
      setIsSearchVisible(true);
      // Focus input after expansion animation
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          updateOverlayPosition();
          try {
            const s = searchRef.current as HTMLIonSearchbarElement | null;
            const getter = s ? (s as unknown as { getInputElement?: () => Promise<HTMLInputElement> }).getInputElement : undefined;
            if (getter) {
              getter().then((inputEl: HTMLInputElement) => {
                try { inputEl.focus(); } catch { }
              }).catch(() => { });
            }
          } catch { }
        });
      });
    }
  }, [isSearchVisible, updateOverlayPosition]);

  const [customerCache, setCustomerCache] = useState<Map<string, CustomerInfo>>(new Map<string, CustomerInfo>());

  // Listen for cache invalidation events (e.g., when contact is created/updated)
  React.useEffect(() => {
    const unsubscribe = onCacheInvalidation(() => {
      console.log('📬 Cache invalidated, clearing customer cache to force refresh');
      setCustomerCache(new Map<string, CustomerInfo>()); // Clear cache
      // This triggers re-renders of components that depend on customerCache
    });

    return unsubscribe; // Cleanup on unmount
  }, []);

  const filteredConversations = useMemo(() => {
    // 🎯 ENHANCED MERGE LOGIC:
    // 1. Start with Wix conversations (canonical source)
    // 2. Add SMS/call items GROUPED by customer (one item per customer with all their comms)
    // 3. Perform customer lookups for SMS/call items to get names/avatars
    // 4. For non-contact calls (no customerId), use phone number as fallback

    type UnifiedConvInfo = { customerId?: string; phoneNumber?: string; isNonContact: boolean };
    const mergedConversations: (Conversation | (Conversation & { _unifiedItem?: boolean; _icon?: string; _badge?: string; _customerInfo?: UnifiedConvInfo }))[] = [];
    const conversationIds = new Set<string>();
    const processedCustomers = new Set<string>();

    // 1️⃣ Add all canonical Wix conversations first. The conversation cache document never
    // stores the contact's avatar itself (only name/lastMessage/etc) — only the linked
    // contactId. So any conversation with a contactId but no cached avatarUrl gets tagged
    // with _customerInfo, same mechanism as the SMS/timeline lookups below, so the async
    // customerCache lookup resolves the real avatar from the contacts collection.
    conversations.forEach(conv => {
      const contactId = conv.contactId;
      if (contactId) processedCustomers.add(contactId);
      const needsAvatarLookup = !!contactId && !conv.avatarUrl;
      mergedConversations.push(
        needsAvatarLookup
          ? { ...conv, _customerInfo: { customerId: contactId, isNonContact: false } }
          : conv
      );
      conversationIds.add(String(conv.id));
    });

    // 2️⃣ Add SMS conversations (Twilio) — skip if the contact already has a Wix conversation.
    // Conversations without a real saved contact name (just a formatted phone number) get
    // tagged with _customerInfo so the async lookup below resolves a real name/avatar for them,
    // same as it already does for unified timeline items.
    smsConversations.forEach(smsConv => {
      if (!conversationIds.has(String(smsConv.id))) {
        const needsLookup = !smsConv.hasContactName;
        mergedConversations.push({
          ...smsConv,
          lastMessage: smsConv.lastMessage,
          _customerInfo: { phoneNumber: smsConv.smsPhone, isNonContact: needsLookup },
        });
        conversationIds.add(String(smsConv.id));
      }
    });

    // 4️⃣ Add drafts ONLY if no canonical conversation exists
    conversationDrafts.forEach(draft => {
      const draftId = String(draft.id);
      if (!conversationIds.has(draftId)) {
        mergedConversations.push(draft);
        console.log(`📝 Showing draft for conversation ${draftId} (no canonical yet)`);
      }
    });

    // 5️⃣ Group timeline items by customer ID (or phone number for non-contacts)
    const timelineByCustomer = new Map<string, typeof unifiedItems>();
    unifiedItems.forEach(item => {
      // Use customerId as key, fall back to phone number for non-contact calls
      // Extract phone from call or sms object if customerId is not available
      let phoneNumber: string | undefined;
      if (!item.customerId) {
        phoneNumber = item.call?.customerNumber || item.sms?.fromNumber || item.sms?.toNumber;
      }

      const key = item.customerId || `phone-${phoneNumber}`;
      if (!timelineByCustomer.has(key)) {
        timelineByCustomer.set(key, []);
      }
      timelineByCustomer.get(key)!.push(item);
    });

    // 6️⃣ Add timeline items as virtual conversations (grouped by customer)
    // NOTE: Customer lookup is async, so we'll schedule it separately
    timelineByCustomer.forEach((items, customerKey) => {
      // Skip if this customer already has a Wix conversation
      const customerId = customerKey.startsWith('phone-') ? undefined : customerKey;
      if (customerId && processedCustomers.has(customerId)) {
        console.log(`⏭️ Skipping timeline items for customer ${customerId} (already has Wix conversation)`);
        return;
      }

      // Get the most recent item to use as preview
      const latestItem = items[0];
      if (!latestItem) return;

      // Extract phone number for non-contact calls
      let phoneNumber: string | undefined;
      if (!customerId) {
        phoneNumber = latestItem.call?.customerNumber || latestItem.sms?.fromNumber || latestItem.sms?.toNumber;
      }

      // Create a virtual "conversation" for this customer's SMS/calls (grouped together)
      const virtualConvId = `timeline-${customerKey}`;
      if (!conversationIds.has(virtualConvId)) {
        // Count how many messages this customer has
        const totalItems = items.length;
        const callCount = items.filter(i => i.type === 'call').length;
        const smsCount = items.filter(i => i.type === 'sms').length;

        // Build a summary badge showing count
        const badge = totalItems > 1 ? `${totalItems} (${callCount}C/${smsCount}S)` : (latestItem.type === 'call' ? 'CALL' : 'SMS');

        const convWithUnified: Conversation & { _unifiedItem: boolean; _icon: typeof call | typeof mail; _badge: string; _customerInfo: UnifiedConvInfo } = {
          id: virtualConvId,
          name: latestItem.preview || `Customer (${latestItem.channel})`,
          messages: [] as Message[],
          timestamp: new Date(latestItem.timestamp),
          avatarUrl: '',
          lastMessage: `[${latestItem.type === 'call' ? '📞' : '💬'} ${latestItem.channel}] ${latestItem.preview}`,
          unread: false,
          starred: false,
          archived: false,
          _unifiedItem: true,
          _icon: latestItem.type === 'call' ? call : mail,
          _badge: badge,
          _customerInfo: {
            customerId: customerId,
            phoneNumber: phoneNumber,
            isNonContact: !customerId // Flag for non-contact calls
          }
        };

        mergedConversations.push(convWithUnified);
        conversationIds.add(virtualConvId);
      }
    });

    console.log('Filtering conversations, total:', mergedConversations.length, `(${conversations.length} Wix + ${conversationDrafts.length} drafts + ${timelineByCustomer.size} SMS/call customers)`);
    if (mergedConversations.length > 0) {
      console.log('📋 Merged order (first 5):', mergedConversations.slice(0, 5).map(c => ({
        name: c.name,
        timestamp: c.timestamp?.toISOString?.(),
        isSmsCall: !!(c as Conversation & { _unifiedItem: boolean })._unifiedItem
      })));
    }

    const result = mergedConversations.filter(conv => {
      // Only actual conversation messages belong in this list. A conversation whose preview is empty has
      // nothing but business notices (email opened/clicked, new order/booking/invoice, "became a member"…)
      // or is an empty shell, and a preview that is itself one of those notices doesn't count either.
      // SMS/call entries always carry a preview, so they pass.
      const preview = (conv.lastMessage ?? '').trim();
      if (!preview || formatBusinessNotificationText(preview) !== preview) return false;

      // Apply search filter
      const matchesSearch = conv.name.toLowerCase().includes(searchText.toLowerCase()) ||
        conv.lastMessage.toLowerCase().includes(searchText.toLowerCase());

      if (!matchesSearch) return false;

      // Apply status filters
      if (activeFilter === 'all') return !conv.archived; // Hide archived in 'all' view
      if (activeFilter === 'unread') return conv.unread && !conv.archived;
      if (activeFilter === 'starred') return conv.starred && !conv.archived;
      if (activeFilter === 'archived') return conv.archived;
      if (activeFilter === 'important') return conv.priority === 'important' && !conv.archived;
      if (activeFilter === 'high') return conv.priority === 'high' && !conv.archived;
      if (activeFilter === 'medium') return conv.priority === 'medium' && !conv.archived;
      if (activeFilter === 'low') return conv.priority === 'low' && !conv.archived;

      return true;
    });

    console.log(`📊 Filter applied (${activeFilter}): ${mergedConversations.length} total → ${result.length} filtered`);
    console.log('Search active:', !!searchText ? `"${searchText}"` : 'none');

    // Sort ONCE here - newest first (only sorting filtered results)
    return result.sort((a, b) => {
      const timeA = a.timestamp?.getTime?.() || 0;
      const timeB = b.timestamp?.getTime?.() || 0;
      return timeB - timeA;
    });
  }, [conversations, conversationDrafts, smsConversations, searchText, activeFilter, unifiedItems]);

  // One row per contact: a contact's SMS / call rows are folded into that contact's Wix conversation row
  // (the thread view already shows their SMS + calls together once opened). The row shows whichever of
  // the two is newer, unread counts add up, and the SMS phone numbers are remembered so opening the row
  // can mark those SMS read. Rows whose contact has no Wix conversation stay as they are.
  const displayConversations = useMemo(() => {
    type MergeableConv = Conversation & { _unifiedItem?: boolean; _customerInfo?: UnifiedConvInfo; _mergedSmsPhones?: string[] };
    const isWixRow = (c: Conversation & { _unifiedItem?: boolean }) =>
      !c._unifiedItem && !String(c.id).startsWith('sms-') && !String(c.id).startsWith('temp-');

    const wixByContact = new Map<string, Conversation>();
    filteredConversations.forEach(conv => {
      if (isWixRow(conv as MergeableConv) && conv.contactId) wixByContact.set(conv.contactId, conv);
    });
    if (wixByContact.size === 0) return filteredConversations;

    const merged = new Map<string, MergeableConv>(); // wix conversation id -> merged copy
    const folded = new Set<Conversation>();
    filteredConversations.forEach(conv => {
      const c = conv as MergeableConv;
      const info = c._customerInfo;
      const isSmsRow = String(conv.id).startsWith('sms-');
      if (!info || !(isSmsRow || c._unifiedItem)) return;
      const key = info.customerId || info.phoneNumber;
      const contactId = info.customerId || (key ? customerCache.get(key)?.id : undefined);
      const wix = contactId ? wixByContact.get(contactId) : undefined;
      if (!wix) return;

      const base = merged.get(String(wix.id)) ?? ({ ...wix } as MergeableConv);
      if ((conv.timestamp?.getTime?.() ?? 0) > (base.timestamp?.getTime?.() ?? 0)) {
        base.lastMessage = conv.lastMessage;
        base.timestamp = conv.timestamp;
      }
      base.unreadCount = (base.unreadCount || 0) + (conv.unreadCount || 0);
      base.unread = !!(base.unread || conv.unread);
      if (isSmsRow && info.phoneNumber) base._mergedSmsPhones = [...(base._mergedSmsPhones ?? []), info.phoneNumber];
      merged.set(String(wix.id), base);
      folded.add(conv);
    });
    if (folded.size === 0) return filteredConversations;

    return filteredConversations
      .filter(c => !folded.has(c))
      .map(c => merged.get(String(c.id)) ?? c)
      .sort((a, b) => (b.timestamp?.getTime?.() ?? 0) - (a.timestamp?.getTime?.() ?? 0));
  }, [filteredConversations, customerCache]);

  // 🔄 Async customer lookup for SMS/call items (separate from useMemo)
  // This allows us to update customer names/avatars as data loads
  React.useEffect(() => {
    const performCustomerLookups = async () => {
      const newCache = new Map(customerCache);
      let hasChanges = false;

      for (const conv of filteredConversations) {
        const convWithInfo = conv as Conversation & { _customerInfo?: UnifiedConvInfo };
        if (!convWithInfo._customerInfo) continue;

        const { customerId, phoneNumber } = convWithInfo._customerInfo;
        const cacheKey = customerId || phoneNumber;
        if (!cacheKey) continue;

        // Skip if already cached
        if (newCache.has(cacheKey)) continue;

        try {
          const info = await getCustomerInfo(customerId, phoneNumber);
          if (info) {
            newCache.set(cacheKey, info);
            hasChanges = true;
          }
        } catch (error) {
          console.error(`❌ Failed to lookup customer ${cacheKey}:`, error);
        }
      }

      if (hasChanges) {
        setCustomerCache(newCache);
      }
    };

    performCustomerLookups();
  }, [filteredConversations, customerCache]);

  React.useEffect(() => {
    if (filteredConversations.length > 0) {
      console.log(`🔍 RENDER ORDER: Rendering ${filteredConversations.length} conversations (first 5):`);
      console.log('🔍 RENDER ORDER (first 5):', filteredConversations.slice(0, 5).map((c, i) => `${i}: ${c.name} (${c.timestamp?.toISOString?.().split('T')[0]})`).join(' | '));
    }
  }, [filteredConversations]);

  /**
   * A text row for a contact opens that contact's one conversation — chat, email and texts together — instead of a
   * text-only thread (which has no Wix messages in it). The conversation is taken from the list when it is there, else
   * asked of Wix (get-or-create: it adds no message). If that fails the text-only thread opens as before.
   */
  const openContactConversation = useCallback(async (
    contact: { id: string; name: string; avatar?: string },
    meta: Partial<InboxConversationSelectMeta>,
    openTextOnly: () => void,
  ) => {
    const base = { contactId: contact.id, contactName: contact.name, avatarUrl: contact.avatar, ...meta };
    const existing = conversations.find(
      c => c.contactId === contact.id && !String(c.id).startsWith('sms-') && !String(c.id).startsWith('temp-'),
    );
    if (existing) {
      onSelectConversation(String(existing.id), { ...base, avatarUrl: contact.avatar || existing.avatarUrl });
      return;
    }
    try {
      const conversation = await InboxService.startConversation(contact.id);
      if (conversation?.id) {
        onSelectConversation(String(conversation.id), base);
        return;
      }
    } catch (err) {
      console.error("Could not open the contact's conversation:", err);
    }
    openTextOnly();
  }, [conversations, onSelectConversation]);

  const handleSelectContact = async (
    contact: ContactListItem,
    channelIntent?: { initialChannel?: 'chat' | 'email' | 'sms'; initialFromEmail?: 'scheduling' | 'billing' | 'support' },
  ) => {
    // Check if conversation already exists with this contact
    const existingConv = conversations.find(
      conv => conv.name === `${contact.firstName} ${contact.lastName}`.trim()
    );

    if (existingConv) {
      // Select existing conversation
      onSelectConversation(String(existingConv.id), { contactName: existingConv.name, avatarUrl: existingConv.avatarUrl, ...channelIntent });
    } else {
      const contactName = `${contact.firstName} ${contact.lastName}`.trim() ||
        contact.displayName ||
        contact.primaryEmail ||
        // i18n-ignore: persisted with the conversation, stays English
        "Unknown Contact" /* i18n-ignore: persisted with the conversation */;

      // Create temp conversation shell to render UI
      const tempId = `temp-${Date.now()}`;
      const tempConversation: Conversation = {
        id: tempId,
        name: contactName,
        messages: [],
        timestamp: new Date(),
        avatarUrl: contact.avatar || '',
        lastMessage: ''
      };

      // Add temp conversation to state and select it
      setConversations([tempConversation, ...conversations]);
      onSelectConversation(tempId, {
        isBlankShell: true,
        contactName: contactName,
        avatarUrl: contact.avatar,
        ...channelIntent,
      });

      // Persist contact metadata for viewport switches (mobile <-> desktop)
      try {
        sessionStorage.setItem(`conversationMeta:${tempId}`, JSON.stringify({
          contactName,
          avatarUrl: contact.avatar || undefined,
        }));
      } catch {
        // ignore
      }

      // Fetch real conversation ID in background
      InboxService.startConversation(contact.id)
        .then((newConversation) => {
          if (newConversation?.id) {
            console.log(`📭 InboxConversationList: Got real conversation ID: ${newConversation.id}, replacing temp: ${tempId}`);

            try {
              sessionStorage.setItem('lastInboxDesktopConversationId', String(newConversation.id));

              // Also persist contact metadata under the real ID
              sessionStorage.setItem(`conversationMeta:${String(newConversation.id)}`, JSON.stringify({
                contactName,
                avatarUrl: contact.avatar || undefined,
              }));
            } catch {
              // ignore
            }

            // Replace temp conversation with real one
            setConversations(prev => {
              // Remove temp conversation and add real one
              const filtered = prev.filter(c => c.id !== tempId);
              return [
                {
                  ...newConversation,
                  name: contactName,
                  avatarUrl: contact.avatar || newConversation.avatarUrl,
                  timestamp: newConversation.timestamp || new Date()
                },
                ...filtered
              ];
            });

            // Dispatch custom event to notify the open thread (ConversationThread) of the real ID.
            // This is the only signal needed -- no URL/history update required here.
            window.dispatchEvent(new CustomEvent('realConversationFound', {
              detail: {
                conversationId: newConversation.id,
                contactName: contactName
              }
            }));
          }
        })
        .catch(err => {
          console.error('Failed to create conversation:', err);
          setConversations(prev => prev.filter(c => c.id !== tempId));
        });
    }
  };

  const handleMarkAllAsRead = () => {
    saveRowsRead([...conversations.filter(c => c.unread), ...smsConversations.filter(c => c.unread)]);
    setConversations(prev => {
      const updated = prev.map(conv => ({ ...conv, unread: false }));
      // Return updated conversations (sorting happens in useMemo)
      return updated;
    });
  };

  const handleToggleEditMode = useCallback(() => {
    setEditMode(prev => !prev);
    setSelectedConversations([]);
  }, []);

  const handleToggleSelection = useCallback((convId: string | number) => {
    setSelectedConversations(prev =>
      prev.includes(convId)
        ? prev.filter(id => id !== convId)
        : [...prev, convId]
    );
  }, []);

  const renderedConversationItems = useMemo(
    () =>
      displayConversations.map((conv, index) => {
        // Get customer info if this is a unified item
        const convWithInfo = conv as Conversation & { _unifiedItem?: boolean; _customerInfo?: UnifiedConvInfo; _badge?: string };

        // Use customer lookup name and avatar if available, otherwise fall back to original.
        // A lookup that found no matching contact returns a generic "Unknown (+1...)" name —
        // that's worse than the already-formatted phone number we already have, so ignore it.
        const rawCustomerInfo = convWithInfo._customerInfo ? customerCache.get(convWithInfo._customerInfo.customerId || convWithInfo._customerInfo.phoneNumber || '') : null;
        const customerInfo = rawCustomerInfo && !rawCustomerInfo.name.startsWith('Unknown (') ? rawCustomerInfo : null;
        const displayName = customerInfo?.name || conv.name;
        const displayAvatar = customerInfo?.avatar || conv.avatarUrl;
        const hasRealName = isRealDisplayName(displayName);

        return (
          <IonItem
            key={conv.id || `temp-${index}-${conv.name}`}
            button
            detail={false}
            className={styles.conversationItem}
            onClick={() => {
              if (!editMode) {
                markConversationTap();
                (convWithInfo as { _mergedSmsPhones?: string[] })._mergedSmsPhones?.forEach(phone => markSmsRead(phone));
                console.log(`📭 Opening conversation: name="${displayName}" id="${conv.id}"`);
                // A text row for a known contact opens the contact's unified conversation (Wix + texts), not a text-only thread
                const textPhone = String(conv.id).startsWith('sms-') ? convWithInfo._customerInfo?.phoneNumber : undefined;
                if (textPhone && customerInfo?.id) {
                  markSmsRead(textPhone);
                  void openContactConversation(
                    { id: customerInfo.id, name: displayName, avatar: displayAvatar },
                    { smsPhones: [textPhone] },
                    () => onSelectConversation(String(conv.id), {
                      contactName: displayName,
                      avatarUrl: displayAvatar,
                      contactId: conv.contactId || undefined,
                      smsPhones: [textPhone],
                    }),
                  );
                  return;
                }
                onSelectConversation(String(conv.id), {
                  contactName: displayName,
                  avatarUrl: displayAvatar,
                  contactId: conv.contactId || undefined,
                  smsPhones: (convWithInfo as { _mergedSmsPhones?: string[] })._mergedSmsPhones,
                });
              } else {
                handleToggleSelection(conv.id);
              }
            }}
          >
            <div className={styles.avatarWrapper} slot="start">
              <IonAvatar className={styles.avatar}>
                {hasRealName || displayAvatar ? (
                  <img
                    src={proxyImage(displayAvatar ?? '') || getDefaultAvatar(displayName)}
                    alt={displayName}
                    onError={(e) => {
                      const imgElement = e.target as HTMLImageElement;
                      imgElement.src = getDefaultAvatar(displayName);
                    }}
                  />
                ) : (
                  <IonIcon icon={person} className={styles.avatarPlaceholderIcon} />
                )}
              </IonAvatar>
              {editMode && selectedConversations.includes(conv.id) && (
                <div className={styles.avatarCheckmark}>
                  <IonIcon icon={checkmarkSharp} />
                </div>
              )}
            </div>
            <IonLabel>
              <IonText className={styles.contactName}>
                <h3>{displayName}</h3>
              </IonText>
              <IonText className={styles.lastMessage}>
                <p>{conv.lastMessage}</p>
              </IonText>
            </IonLabel>
            <div className={styles.conversationMeta} slot="end">
              <IonText className={styles.timestamp}>
                <p>{formatTimestamp(conv.timestamp)}</p>
              </IonText>
              <div className={styles.iconsRow}>
                {convWithInfo._unifiedItem && (
                  <IonBadge color="secondary" className={styles.unreadBadge}>
                    {convWithInfo._badge}
                  </IonBadge>
                )}
                {conv.starred && (
                  <IonIcon icon={star} className={styles.starIcon} color="warning" />
                )}
                {conv.unread && !convWithInfo._unifiedItem && (
                  <IonBadge color="primary" className={styles.unreadBadge}>
                    {conv.unreadCount || 1}
                  </IonBadge>
                )}
              </div>
            </div>
          </IonItem>
        );
      }),
    [displayConversations, editMode, selectedConversations, onSelectConversation, handleToggleSelection, customerCache, openContactConversation]
  );

  // Date sections over the (newest-first) filtered list — indices point into renderedConversationItems
  const conversationGroups = useMemo(() => {
    const now = new Date();
    const groups: { key: string; label: string; indices: number[] }[] = [];
    displayConversations.forEach((conv, index) => {
      const { key, label } = getDateGroup(conv.timestamp, now, t);
      const last = groups[groups.length - 1];
      if (last && last.key === key) last.indices.push(index);
      else groups.push({ key, label, indices: [index] });
    });
    return groups;
  }, [displayConversations, t]);

  const handleBatchAction = (action: 'archive' | 'delete' | 'read' | 'star') => {
    if (action === 'read') {
      saveRowsRead(displayConversations.filter(c => selectedConversations.includes(c.id)));
    }
    if (action === 'archive') {
      // viewing the Archived filter: this un-archives, anywhere else it archives
      saveRowsArchived(displayConversations.filter(c => selectedConversations.includes(c.id)), activeFilter !== 'archived');
    }
    setConversations(prev => {
      let updated = [...prev];

      switch (action) {
        case 'archive': {
          // Toggle archive: if viewing archived filter, unarchive. Otherwise, archive.
          const shouldArchive = activeFilter !== 'archived';
          updated = prev.map(conv =>
            selectedConversations.includes(conv.id) ? { ...conv, archived: shouldArchive } : conv
          );
          break;
        }
        case 'delete':
          updated = prev.filter(conv => !selectedConversations.includes(conv.id));
          setSelectedConversations([]);
          break;
        case 'read':
          updated = prev.map(conv =>
            selectedConversations.includes(conv.id) ? { ...conv, unread: false, unreadCount: 0 } : conv
          );
          break;
        case 'star':
          updated = prev.map(conv =>
            selectedConversations.includes(conv.id) ? { ...conv, starred: !conv.starred } : conv
          );
          break;
      }

      console.log('Batch action:', action, 'Updated:', updated);

      // Return updated conversations (sorting happens in useMemo)
      return updated;
    });
    // Don't clear selection or exit edit mode - user can do multiple actions
  };

  const handleDoneEditing = () => {
    setSelectedConversations([]);
    setEditMode(false);
  };

  const applyProgress = useCallback((progress: number, _smooth: boolean) => {
    baseHeaderRef.current?.style.setProperty('--title-opacity', String(progress));
    if (titleCardAnimationRef.current) {
      titleCardAnimationRef.current.style.setProperty('--content-opacity', String(1 - progress));
      titleCardAnimationRef.current.style.setProperty('--content-scale', `scale(${1 - (progress * 0.2)})`);
    }
  }, []);

  const handleScroll = useScrollSnap(gridRef, 66, applyProgress);

  // The list is rebuilt while the page is hidden, so it is back at the top on return. The
  // header (title card / small title) follows scroll events, which a rebuilt list at scroll 0
  // never fires, so it is put back to expanded explicitly.
  const scrollResetKey = useRemountOnPageHidden(gridRef);
  useEffect(() => {
    applyProgress(0, false);
    wasAtTop.current = true;
  }, [scrollResetKey, applyProgress]);

  // Until the role is checked, and for good when it is not allowed, nothing of the page is live: not the header buttons, the
  // contact picker, the new-chat sheet or the editing menu
  if (access !== 'allowed') {
    return <AccessDeniedPage checking={access === 'checking'} onClose={onClose} />;
  }

  return (
    <BasePageWithHeader
      ref={baseHeaderRef}
      title={t('components.InboxConversationList.inbox')}
      toolbarClassName={styles.toolbar}
      smallTitleClassName={styles.smallTitle}
      titleHeaderClassName={styles.titleHeader}
      startButtons={
        <>
          {/* In the softphone the X is first (far left, like the keypad/recents tabs) */}
          {onClose && (
            <IonButton shape="round" className={styles.closeButton} onClick={onClose}>
              <IonIcon icon={closeOutline} />
            </IonButton>
          )}
          <IonButton
            shape="round"
            className={styles.newConversationButton}
            onClick={openContactPicker}
          >
            <IonIcon icon={createOutline} />
          </IonButton>
          {!onClose && (
            <IonButton
              shape="round"
              className={styles.hiddenBalanceButton}
              disabled
              style={{ visibility: 'hidden', pointerEvents: 'none' }}
            >
              <IonIcon icon={ellipsisVertical} />
            </IonButton>
          )}
        </>
      }
      endButtons={
        <>
          <IonButton
            shape="round"
            className={styles.searchButton}
            onClick={handleToggleSearch}
          >
            <IonIcon icon={isSearchVisible ? closeOutline : searchOutline} />
          </IonButton>
          <IonButton
            ref={menuButtonRef}
            shape="round"
            className={styles.menuButton}
            onClick={() => setShowMenu(open => !open)}
          >
            <IonIcon icon={ellipsisVertical} />
          </IonButton>
        </>
      }
    >
      <IonContent scrollY={false} className={styles.content}>
        <PositionedRefresher
          titleRef={titleCardRef}
          contentRef={gridRef}
          offset={16}
          anchor="bottom"
          id="inbox-refresher"
          className={styles.inboxRefresher}
          pullMin={120}
          pullMax={200}
          pullFactor={0.6}
          disabled={refresherDisabled}
          style={{
            '--ion-color-step-250': 'var(--ion-background-color)',
            '--ion-color-step-200': 'var(--ion-background-color)',
            '--ion-background-color': 'transparent',
          } as React.CSSProperties}
          onIonRefresh={(e) => {
            setIsLoading(true);
            setConversations([]);
            const loadConversations = async () => {
              try {
                const dataService = DataServiceFactory.getService();
                const data = await dataService.getConversations?.();
                setConversations(data || []);
              } catch (error) {
                console.error('Error refreshing conversations:', error);
              } finally {
                setIsLoading(false);
                e.detail.complete();
              }
            };
            loadConversations();
          }}
        >
          <IonRefresherContent
            pullingIcon="bubbles"
            refreshingSpinner="bubbles"
          />
        </PositionedRefresher>

        <IonGrid
          key={scrollResetKey}
          ref={gridRef}
          className={styles.inboxContainer}
          style={keyboardHeight > 0 ? { paddingBottom: `${keyboardHeight + KEYBOARD_GAP_PX + 60}px` } : undefined}
          onScroll={(e) => {
            const top = (e.target as HTMLElement).scrollTop || 0;
            const atTop = top <= 5;
            if (atTop !== wasAtTop.current) {
              wasAtTop.current = atTop;
              setRefresherDisabled(!atTop);
            }
            handleScroll(top);
          }}
        >

          <div ref={titleCardRef} className={styles.titleCardWrapper}>
            <TitleCard
              ref={titleCardAnimationRef}
              title={t('components.InboxConversationList.inbox')}
              cardClassName={styles.inboxTitleCard}
              titleContainerClassName={styles.titleContainer}
              titleClassName={styles.inboxTitle}
            />
          </div>

          {/* Edit Mode Actions Bar */}
          {editMode && (
            <IonToolbar className={styles.editActionsBar}>
              <IonButtons slot="start">
                <IonText className={styles.selectedCount}>{t('components.InboxConversationList.selected', { length: selectedConversations.length })}</IonText>
              </IonButtons>
              <IonButtons className={styles.centerButtons}>
                <IonButton fill="clear" onClick={() => handleBatchAction('star')} disabled={selectedConversations.length === 0}>
                  <IonIcon slot="icon-only" icon={star} color="warning" />
                </IonButton>
                <IonButton fill="clear" onClick={() => handleBatchAction('read')} disabled={selectedConversations.length === 0}>
                  <IonIcon slot="icon-only" icon={mailOpen} color="primary" />
                </IonButton>
                <IonButton fill="clear" onClick={() => handleBatchAction('archive')} disabled={selectedConversations.length === 0}>
                  <IonIcon slot="icon-only" icon={archiveOutline} color="primary" />
                </IonButton>
                <IonButton fill="clear" onClick={() => handleBatchAction('delete')} disabled={selectedConversations.length === 0}>
                  <IonIcon slot="icon-only" icon={trashOutline} color="danger" />
                </IonButton>
              </IonButtons>
              <IonButtons slot="end">
                <IonButton
                  className={styles.doneButton}
                  onClick={handleDoneEditing}
                >{t('components.InboxConversationList.done')}
                </IonButton>
              </IonButtons>
            </IonToolbar>
          )}

          <IonList className={`${styles.conversationList} ${styles.skeletonOverlay}`}>
            {isLoading ? (
              /* Placeholder date sections: the same cards as the loaded list (heading over a glass card of
                 rows). Heading text is date data, so it is a bar; row sizes mirror the real rows (40px
                 avatar, 16px name, 14px message, 12px timestamp over a 20px badge). */
              <div className={styles.dateGroups} aria-hidden="true">
                {[3, 5, 4].map((rows, group) => (
                  <div key={group} className={styles.dateGroup}>
                    <IonText className={styles.dateHeading}>
                      <IonSkeletonText animated className={styles.skeletonBar} style={{ width: 96, height: 13, margin: 0 }} />
                    </IonText>
                    <IonList className={styles.conversationCard}>
                      {Array.from({ length: rows }, (_, index) => (
                        <IonItem key={index} className={styles.conversationItem}>
                          <IonSkeletonText animated slot="start" className={styles.skeletonBar} style={{ width: 40, height: 40, margin: 0, borderRadius: '50%' }} />
                          <IonLabel>
                            <IonSkeletonText animated className={styles.skeletonBar} style={{ width: '45%', height: 16, margin: '1px 0 6px' }} />
                            <IonSkeletonText animated className={styles.skeletonBar} style={{ width: '80%', height: 14, margin: '1px 0' }} />
                          </IonLabel>
                          <div className={styles.skeletonMeta} slot="end">
                            <IonSkeletonText animated className={styles.skeletonBar} style={{ width: 28, height: 12, margin: '1px 0' }} />
                            <IonSkeletonText animated className={styles.skeletonBar} style={{ width: 20, height: 20, margin: 0, borderRadius: '50%' }} />
                          </div>
                        </IonItem>
                      ))}
                    </IonList>
                  </div>
                ))}
              </div>
            ) : filteredConversations.length === 0 ? (
              <div className={styles.emptyState}>
                <IonIcon icon={chatbubbleOutline} className={styles.emptyIcon} />
                <IonText className={styles.emptyText}>
                  <h3>{t('components.InboxConversationList.noConversationsFound')}</h3>
                  <p>{searchText ? t('components.InboxConversationList.tryADifferentSearchTerm') : t('components.InboxConversationList.yourMessagesWillAppearHere')}</p>
                </IonText>
              </div>
            ) : (
              <div className={styles.dateGroups}>
                {conversationGroups.map((group) => (
                  <div key={group.key} className={styles.dateGroup}>
                    <IonText className={styles.dateHeading}>{group.label}</IonText>
                    <IonList className={styles.conversationCard}>
                      {group.indices.map((i) => renderedConversationItems[i])}
                    </IonList>
                  </div>
                ))}
              </div>
            )}
          </IonList>
        </IonGrid>

      </IonContent>

      {/* Morphing search overlay + filter button (outside IonContent, inside BasePageWithHeader) */}
      <div
        className={`${styles.searchRow} ${isSearchHidden && !isSearchClosing ? styles.hidden : ''}`}
        style={keyboardHeight > 0 ? { bottom: `${keyboardHeight + KEYBOARD_GAP_PX}px` } : undefined}
      >
        <IonFab
          ref={el => { overlayRef.current = el; }}
          className={`${styles.searchMorph} ${isSearchVisible ? styles.searchMorphExpanded : ''} ${isSearchClosing ? styles.searchMorphClosing : ''} ${isSearchHidden ? styles.hidden : ''}`}
          aria-hidden={!isSearchVisible}
          style={keyboardHeight > 0 ? ({ '--overlay-bottom': `${keyboardHeight + KEYBOARD_GAP_PX}px` } as React.CSSProperties) : undefined}
        >
          <IonSearchbar
            ref={searchRef}
            value={searchText}
            debounce={50}
            onIonInput={e => setSearchText(e.detail.value ?? '')}
            onIonClear={() => setSearchText('')}
            placeholder={t('components.InboxConversationList.searchConversations')}
            animated
            showCancelButton="never"
            showClearButton={searchText ? 'always' : 'never'}
            className={`${styles.searchBar} ${styles.floatingSearchBarInline} ${isSearchVisible ? styles.floatingSearchBarInlineExpanded : ''}`}
          />
        </IonFab>
        <IonButton
          fill="clear"
          className={`${styles.filterButton} ${isSearchVisible ? '' : styles.hidden}`}
          onClick={() => setShowFilterModal(true)}
        >
          <IonIcon icon={filterOutline} slot="icon-only" />
        </IonButton>
      </div>

      {/* New chat: the shared contact picker sheet (always mounted, toggled by isOpen) */}
      <ContactPickerSheet
        isOpen={showContactPicker}
        onClose={() => setShowContactPicker(false)}
        onSelectContact={handleSelectContact}
        title={t('components.InboxConversationList.newMessage')}
        searchPlaceholder="To: (name, phone, or email)"
        outsideRouter={!!onClose}
      />

      {/* Filter Modal */}
      <SheetModal
        isOpen={showFilterModal}
        onClose={() => setShowFilterModal(false)}
        title={t('components.InboxConversationList.filterConversations')}
        menuOptions={[
          {
            label: 'All Conversations',
            value: 'all',
            count: conversations.length,
            chipColor: 'primary',
            handler: () => {
              setActiveFilter('all');
              setShowFilterModal(false);
            }
          },
          {
            label: 'Unread',
            value: 'unread',
            count: conversations.filter(c => c.unread).length,
            chipColor: 'danger',
            handler: () => {
              setActiveFilter('unread');
              setShowFilterModal(false);
            }
          },
          {
            label: 'Starred',
            value: 'starred',
            count: conversations.filter(c => c.starred).length,
            chipColor: 'warning',
            handler: () => {
              setActiveFilter('starred');
              setShowFilterModal(false);
            }
          },
          {
            label: 'Important Priority',
            value: 'important',
            count: conversations.filter(c => c.priority === 'important').length,
            chipColor: 'danger',
            handler: () => {
              setActiveFilter('important');
              setShowFilterModal(false);
            }
          },
          {
            label: 'High Priority',
            value: 'high',
            count: conversations.filter(c => c.priority === 'high').length,
            chipColor: 'warning',
            handler: () => {
              setActiveFilter('high');
              setShowFilterModal(false);
            }
          },
          {
            label: 'Medium Priority',
            value: 'medium',
            count: conversations.filter(c => c.priority === 'medium').length,
            chipColor: 'tertiary',
            handler: () => {
              setActiveFilter('medium');
              setShowFilterModal(false);
            }
          },
          {
            label: 'Low Priority',
            value: 'low',
            count: conversations.filter(c => c.priority === 'low').length,
            chipColor: 'medium',
            handler: () => {
              setActiveFilter('low');
              setShowFilterModal(false);
            }
          },
          {
            label: 'Archived',
            value: 'archived',
            count: conversations.filter(c => c.archived).length,
            chipColor: 'medium',
            handler: () => {
              setActiveFilter('archived');
              setShowFilterModal(false);
            }
          }
        ].map(option => ({
          ...option,
          onClick: option.handler,
          labelContent: (
            <IonItem lines="none" className={styles.filterItem}>
              <IonLabel>{option.label}</IonLabel>
              <IonChip color={option.chipColor} slot="end">
                <IonLabel>{option.count}</IonLabel>
              </IonChip>
            </IonItem>
          )
        }))}
      />

      {/* ⋮ menu — portaled glass card, same as the sales order / invoice ⋮ menu */}
      {showMenu && createPortal(
        <>
          {/* Blurred scrim: softens the page, and a tap on it only closes the menu (it can't fall through) */}
          <div
            className={styles.ellipsisBackdrop}
            onPointerDown={(e) => { e.preventDefault(); e.stopPropagation(); }}
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); setShowMenu(false); }}
          />
          <div ref={menuCardRef} className={styles.ellipsisMenu} role="menu">
            <button type="button" role="menuitem" className={styles.ellipsisMenuItem} onClick={() => { setShowMenu(false); handleMarkAllAsRead(); }}>
              {t('components.InboxConversationList.markAllAsRead')}
            </button>
            <button type="button" role="menuitem" className={styles.ellipsisMenuItem} onClick={() => { setShowMenu(false); handleToggleEditMode(); }}>
              {t('components.InboxConversationList.editList')}
            </button>
            <button type="button" role="menuitem" className={styles.ellipsisMenuItem} onClick={() => { setShowMenu(false); setShowInboxSettingsModal(true); }}>
              {t('components.InboxConversationList.settings')}
            </button>
          </div>
        </>,
        document.body,
      )}

      <IonModal
        isOpen={showInboxSettingsModal}
        onDidDismiss={() => setShowInboxSettingsModal(false)}
        handle={false}
      >
        <InboxSettingsModal
          onClose={() => setShowInboxSettingsModal(false)}
          onOpenTemplates={() => {
            setShowInboxSettingsModal(false);
            setShowTemplatesModal(true);
          }}
          onOpenInboxNotifications={() => {
            setShowInboxSettingsModal(false);
            setShowInboxNotificationsSettingsModal(true);
          }}
        />
      </IonModal>

      <IonModal
        isOpen={showTemplatesModal}
        onDidDismiss={() => setShowTemplatesModal(false)}
        handle={false}
      >
        <TemplatesModal
          onDismiss={() => setShowTemplatesModal(false)}
          onSelectTemplate={() => {
            setShowTemplatesModal(false);
          }}
        />
      </IonModal>

      <InboxNotificationsSettingsModal
        isOpen={showInboxNotificationsSettingsModal}
        onClose={() => setShowInboxNotificationsSettingsModal(false)}
        onBack={() => {
          setShowInboxNotificationsSettingsModal(false);
          setShowInboxSettingsModal(true);
        }}
      />
    </BasePageWithHeader>
  );
};

// Memoised: the Inbox page re-renders when a conversation is opened (the URL changes while it is
// still on screen sliding out), and re-rendering this whole list in the middle of that transition
// delayed the conversation page appearing. With a stable onSelectConversation it is skipped.
export default React.memo(InboxConversationList);
